<template>
  <section-page
    title="Outbound Email Stats"
    full-height
  >
    <template v-slot:right>
      <filter-field-default
        label="time"
        v-model="timeframesFilter"
      >
        <option
            v-for="(time, index) in timeframes"
            :value="time.value"
            :key="index"
        >{{ time.label }}</option>
      </filter-field-default>
    </template>

    <app-loading-section :loading="isLoading">
      <!--Chart-->
      <chart-line
        :data-is-loaded="chartDataIsLoaded"
        :chart-data="chartData"
      ></chart-line>

      <!--Totals-->
      <h2 class="totals_heading">Totals for {{ currentTimeframeLabel }}</h2>

      <div class="flex flex-between flex-wrap">
        <div
          v-for="total in totals"
          :key="total.key"
          class="total"
        >
          <p class="total--label" :style="'color: ' + total.color">{{ total.label }}</p>
          <p class="total--count">{{ total.count }}</p>
          <p class="total--percent">{{ total.percent }}</p>
          <button-link
            v-if="total.type != null"
            @click="openLogModal(total.type, total.label)"
          >
            <strong>View Log</strong>
          </button-link>
        </div>
      </div>

      <!--Table-->
      <table-default
        class="mt-100"
        :table-data="emailTableData"
        default-sort="time"
        default-sort-direction="desc"
      >
        <b-table-column :label="emailData.timeLabel" field="time" sortable v-slot="props">
          {{ props.row.time }}
        </b-table-column>

        <b-table-column :label="labels.delivered" field="delivered" sortable v-slot="props">
          {{ props.row.delivered }}
        </b-table-column>

        <b-table-column :label="labels.failed" field="failed" sortable v-slot="props">
          {{ props.row.failed }}
        </b-table-column>

        <b-table-column :label="labels.opened" field="opened" sortable v-slot="props">
          {{ props.row.opened }}
        </b-table-column>

        <b-table-column :label="labels.clicked" field="clicked" sortable v-slot="props">
          {{ props.row.clicked }}
        </b-table-column>

        <b-table-column :label="labels.unsubscribed" field="unsubscribed" sortable v-slot="props">
          {{ props.row.unsubscribed }}
        </b-table-column>

        <b-table-column :label="labels.complained" field="complained" sortable v-slot="props">
          {{ props.row.complained }}
        </b-table-column>
      </table-default>

      <!--Log Modal-->
      <modal-before-container>
        <modal-default
          ref="emailLogModal"
          :title="emailLogModalTitle"
          is-triple-column
        >
          <app-loading-section :loading="isLoadingViewLog">
            <table-default
              :table-data="logTableData"
            >
              <b-table-column label="To" field="to" sortable v-slot="props">
                {{ props.row.tos }}
              </b-table-column>

              <b-table-column label="From" field="from" sortable v-slot="props">
                {{ props.row.email_from.email_address }}
              </b-table-column>

              <b-table-column label="Subject" field="subject" sortable v-slot="props">
                {{ props.row.email_subject }}
              </b-table-column>

              <b-table-column label="Sent" field="sent" sortable v-slot="props">
                {{ formatDate(props.row.sent) }}
              </b-table-column>

              <b-table-column cell-class="action_cell" v-slot="props" width="215">
                <button-link
                  v-if="props.row.contact_id"
                  class="ml-100"
                  @click="openEmailPreview(props.row)"
                >View Email</button-link>

                <button-link
                  v-if="props.row.contact_id"
                  class="ml-100"
                  @click="openContactModal(props.row.contact_id)"
                >Contact</button-link>

                <button-link
                  v-if="props.row.record_id"
                  @click="openRecordModal(props.row.record_id)"
                >Record</button-link>
              </b-table-column>
            </table-default>
          </app-loading-section>
        </modal-default>
      </modal-before-container>

      <!--Email Preview-->
      <modal-container>
        <modal-default
          ref="emailPreviewModal"
          title="Email Preview"
          is-double-column
        >
          <email-preview :email="currentEmail"></email-preview>
        </modal-default>
      </modal-container>
    </app-loading-section>
  </section-page>
</template>

<script>
import {timeframesEmail} from '../../services/staticData'
import {ENUMS} from "../../services/enums"
import EmailPreview from "../../components/comms/components/EmailPreview"
import dates from "../../services/dates";
import router from "../../router";

export default {
  name: "StatsEmailTab",
  components: {EmailPreview},
  data() {
    return {
      isLoading: false,
      isLoadingViewLog: false,
      chartDataIsLoaded: false,
      emailData: {},
      currentEmail: {
          to: {
            name: null,
            email: null,
          },
          from: {
            name: null,
            email: null,
          },
          sent: null,
          email_subject: null,
          email_body: null,
          contact_id: null,
      },
      colors: {
        delivered: '#0B9E34',
        failed: '#C22314',
        opened: '#7030C9',
        clicked: '#318DDE',
        unsubscribed: '#F79A36',
        complained: '#DB3052',
      },
      labels: {
        delivered: ENUMS.MailGunEventEnum.Delivered.label,
        failed: ENUMS.MailGunEventEnum.Failed.label,
        opened: ENUMS.MailGunEventEnum.Opened.label,
        clicked: ENUMS.MailGunEventEnum.Clicked.label,
        unsubscribed: ENUMS.MailGunEventEnum.Unsubscribed.label,
        complained: ENUMS.MailGunEventEnum.Complained.label,
      },
      logTableData: [],
      emailLogModalTitle: 'Email Log'
    }
  },
  computed: {
    timeframes() {
      return [
        ...timeframesEmail
      ];
    },
    timeframesFilter: {
      get() {
        return this.$store.state.stats.filters.email.timeframesFilter
      },
      set(val) {
        this.$store.commit('stats/setOneFilter', ['email', 'timeframesFilter', val])
        this.getEmailData();
      }
    },
    currentTimeframeLabel() {
      for (let i=0; i < this.timeframes.length; i++) {
        if (this.timeframes[i].value === this.timeframesFilter) {
            return this.timeframes[i].label;
        }
      }
    },
    currentTimeframeDates() {
      for (let i=0; i < this.timeframes.length; i++) {
        if (this.timeframes[i].value === this.timeframesFilter) {
          return {
            start_at: this.timeframes[i].start,
            end_at: this.timeframes[i].end
          }
        }
      }
    },
    chartData() {
      return {
        labels: this.emailData.times,
        datasets: [
          {
            label: this.labels.delivered,
            data: this.emailData.delivered,
            fill: false,
            borderColor: this.colors.delivered,
            tension: 0
          },
          {
            label: this.labels.failed,
            data: this.emailData.failed,
            fill: false,
            borderColor: this.colors.failed,
            tension: 0
          },
          {
            label: this.labels.opened,
            data: this.emailData.opened,
            fill: false,
            borderColor: this.colors.opened,
            tension: 0
          },
          {
            label: this.labels.clicked,
            data: this.emailData.clicked,
            fill: false,
            borderColor: this.colors.clicked,
            tension: 0
          },
          {
            label: this.labels.unsubscribed,
            data: this.emailData.unsubscribed,
            fill: false,
            borderColor: this.colors.unsubscribed,
            tension: 0
          },
          {
            label: this.labels.complained,
            data: this.emailData.complained,
            fill: false,
            borderColor: this.colors.complained,
            tension: 0
          },
        ]
      }
    },
    totals() {
      const delivered = this.getCountTotal(this.emailData.delivered);
      const failed = this.getCountTotal(this.emailData.failed);
      const opened = this.getCountTotal(this.emailData.opened);
      const clicked = this.getCountTotal(this.emailData.clicked);
      const unsubscribed = this.getCountTotal(this.emailData.unsubscribed);
      const complained = this.getCountTotal(this.emailData.complained);

      return [
        {
          key: 1,
          label: this.labels.delivered,
          color: this.colors.delivered,
          count: delivered,
          percent: null,
          type: null,
        },
        {
          key: 2,
          label: this.labels.failed,
          color: this.colors.failed,
          count: failed,
          percent: this.getPercent(failed, delivered),
          type: ENUMS.MailGunEventEnum.Failed.value,
        },
        {
          key: 3,
          label: this.labels.opened,
          color: this.colors.opened,
          count: opened,
          percent: this.getPercent(opened, delivered),
          type: ENUMS.MailGunEventEnum.Opened.value,
        },
        {
          key: 4,
          label: this.labels.clicked,
          color: this.colors.clicked,
          count: clicked,
          percent: this.getPercent(clicked, delivered),
          type: ENUMS.MailGunEventEnum.Clicked.value,
        },
        {
          key: 5,
          label: this.labels.unsubscribed,
          color: this.colors.unsubscribed,
          count: unsubscribed,
          percent: this.getPercent(unsubscribed, delivered),
          type: ENUMS.MailGunEventEnum.Unsubscribed.value,
        },
        {
          key: 6,
          label: this.labels.complained,
          color: this.colors.complained,
          count: complained,
          percent: this.getPercent(complained, delivered),
          type: ENUMS.MailGunEventEnum.Complained.value,
        },
      ];
    },
    emailTableData() {
      if (this.isLoading) return [];

      const emailData = this.emailData;
      return emailData.times.map(function (value, index) {
        // Return '--' if 0 because it makes the actual counts stand out more for the user
        return {
          time: value,
          delivered: emailData.delivered[index] === 0 ? '--' : emailData.delivered[index],
          failed: emailData.failed[index] === 0 ? '--' : emailData.failed[index],
          opened: emailData.opened[index] === 0 ? '--' : emailData.opened[index],
          clicked: emailData.clicked[index] === 0 ? '--' : emailData.clicked[index],
          unsubscribed: emailData.unsubscribed[index] === 0 ? '--' : emailData.unsubscribed[index],
          complained: emailData.complained[index] === 0 ? '--' : emailData.complained[index],
        }
      });
    }
  },
  created() {
    this.getEmailData();
  },
  methods: {
    formatDate(date) {
      return dates.formatAsDate(date);
    },
    getEmailData() {
      const payload = {
        start_at: this.currentTimeframeDates.start_at,
        end_at: this.currentTimeframeDates.end_at,
      }

      this.isLoading = true;
      this.$api.stats.getEmailStats(payload)
        .then((res) => {
          this.emailData = res.data
          this.chartDataIsLoaded = true;
        })
        .finally(() => this.isLoading = false)
    },
    getCountTotal(values) {
      if (values === undefined) return 0;

      let count = 0;
      values.forEach(value => {count += parseInt(value)});
      return count;
    },
    getPercent(top, bottom) {
      if(bottom <= 0) {
        return '0%';
      }

      let number = Math.round((top/bottom) * 100);

      return number + '%';
    },
    openLogModal(type, label) {
      this.emailLogModalTitle = label + ' Email Log';
      const payload = {
        start_at: this.currentTimeframeDates.start_at,
        end_at: this.currentTimeframeDates.end_at,
        type: type
      }

      this.isLoadingViewLog = true
      this.$api.stats.getEmailStatsLog(payload)
        .then((res) => {
          this.logTableData = res.data.map(email => {
            email['tos'] = email.email_to.map(to => {
              return to.email_address
            }).join(', ')
            return email
          })
        })
        .finally(() => this.isLoadingViewLog = false)

      this.$refs.emailLogModal.open();
    },
    openEmailPreview(email) {
      this.currentEmail = email;
      this.$refs.emailPreviewModal.open();
    },
    openContactModal(contactId) {
      router.replace({query: {contact: contactId}});
    },
    openRecordModal(recordId) {
      router.replace({query: {lead: recordId}});
    },
  },
}
</script>

<style lang="scss" scoped>
@import "../../styles/variables/variables";

.totals_heading {
  margin: 1rem 0;
  font-weight: 500;
  color: $text-light;
}

.total {
  width: calc(100% / 6);
  margin-bottom: 2rem;

  @include mobile() {
    width: calc(100% / 3);
  }

  &--label {
    margin-bottom: 2px;
    font-size: 0.75rem;
    font-weight: bold;
  }

  &--count {
    font-size: 54px;
  }

  &--percent {
    margin: 4px 0 8px;
    color: $text-light;
  }
}
</style>
